import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import TextDensityCompressIcon from '@atlaskit/icon-lab/core/text-density-compress';
import TextDensityExpandIcon from '@atlaskit/icon-lab/core/text-density-expand';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';

import type { UiContentMode } from '../utils/contentMode.ts';
import { useEditorContentMode } from '../hooks/useEditorContentMode.ts';

type Props = {
	contentId: string;
	disabled?: boolean;
	isStatusDisconnected?: boolean;
};

const messages = defineMessages({
	makeCompact: {
		id: 'editor-content-mode-button.make.compact',
		defaultMessage: 'Use compact density',
		description:
			'The text is shown as a drop-down menu item label in the editor content mode button. Selecting this option switches the editor content density to compact mode, reducing spacing between elements.',
	},
	makeStandard: {
		id: 'editor-content-mode-button.make.standard',
		defaultMessage: 'Use standard density',
		description:
			'The text is shown as a drop-down menu item label in the editor content mode button. Selecting this option switches the editor content density to standard mode, using the default spacing between elements.',
	},
});

export const EditorContentModeButton: React.FC<Props> = ({
	contentId,
	disabled = false,
	isStatusDisconnected = false,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const { mode, isSaving, toggle } = useEditorContentMode({ contentId });
	const isCompact = mode === 'compact';

	const label = intl.formatMessage(isCompact ? messages.makeStandard : messages.makeCompact);
	const Icon = useMemo(
		() => (isCompact ? TextDensityExpandIcon : TextDensityCompressIcon),
		[isCompact],
	);

	const isBlocked = disabled || isStatusDisconnected || isSaving;

	const fireAnalytics = useCallback(
		(from: UiContentMode, to: UiContentMode) => {
			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					action: 'updateContentMode',
					actionSubject: 'updateContentMode',
					source: 'EditorContentModeButton',
					attributes: { contentId, previousMode: from, mode: to },
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	const runEditorContentModeCommand = useCallback(
		(nextMode: UiContentMode) => {
			const command =
				fullPageEditorPresetAPI?.contentFormat?.commands?.updateContentMode?.(nextMode);
			if (!command) {
				return;
			}
			fullPageEditorPresetAPI?.core?.actions?.execute(command);
		},
		[fullPageEditorPresetAPI],
	);

	const handleClick = useCallback(async () => {
		if (!contentId || isStatusDisconnected || isSaving) return;
		const { ok, from, to } = await toggle();
		if (ok) {
			fireAnalytics(from, to);
			runEditorContentModeCommand(to);
		}
	}, [
		contentId,
		isStatusDisconnected,
		isSaving,
		toggle,
		fireAnalytics,
		runEditorContentModeCommand,
	]);

	return (
		<IconButton
			appearance="subtle"
			spacing="default"
			isDisabled={isBlocked || isSaving}
			onClick={handleClick}
			isTooltipDisabled={false}
			label={label}
			icon={(props) => <Icon {...props} label={label} />}
			testId="editor-content-mode-button"
		/>
	);
};
